import React, { useEffect, useMemo, useRef, useState } from "react";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import { ModalOverlay } from "../../../components/ModalOverlay";
import CertificateCanvas from "../../setting/manageprint/certificatePrint/CertificateCanvas";
import {
  findTemplate,
  loadCertificateStore,
} from "../../setting/manageprint/certificatePrint/certificateStorage";
import {
  buildCertificatePrintData,
  formatCertificateDate,
  inclusiveLeaveDays,
} from "../../setting/manageprint/certificatePrint/buildCertificatePrintData";
import { DEFAULT_CERTIFICATE_LOGO } from "../../setting/manageprint/certificatePrint/certificateTemplates";
import { useLoggedInClinic } from "../../setting/manageprint/certificatePrint/useLoggedInClinic";
import { isRealClinicValue } from "../../setting/manageprint/certificatePrint/getLoggedInClinic";
import { printCertificateSheet } from "../../setting/manageprint/certificatePrint/printCertificateSheet";
import type { SummaryTheme } from "./patientSummaryUi";
import "../../setting/manageprint/certificatePrint/certificatePrint.css";

type PatientCertificatePrintModalProps = {
  open: boolean;
  onClose: () => void;
  patient: any;
  treatments?: any[];
  theme?: SummaryTheme;
};

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function waitForPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function PatientCertificatePrintModal({
  open,
  onClose,
  patient,
  treatments = [],
  theme = "light",
}: PatientCertificatePrintModalProps) {
  const clinic = useLoggedInClinic(open);
  const store = useMemo(() => loadCertificateStore(), [open]);
  const [templateId, setTemplateId] = useState(store.defaultId);
  const [leaveStart, setLeaveStart] = useState(isoToday);
  const [leaveEnd, setLeaveEnd] = useState(isoToday);
  const [reason, setReason] = useState("Dental treatment");
  const [recommendation, setRecommendation] = useState(
    "Rest as advised and attend the next review appointment."
  );
  const [printing, setPrinting] = useState(false);
  const paperRef = useRef<HTMLDivElement>(null);
  const isDark = theme === "dark";

  useEffect(() => {
    if (!open) return;
    setTemplateId(loadCertificateStore().defaultId);
    setLeaveStart(isoToday());
    setLeaveEnd(isoToday());
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, patient?._id]);

  const template = findTemplate(store.templates, templateId);
  const clinicLogo = String(clinic.printImages ?? "");
  const usableLogo =
    isRealClinicValue(clinicLogo) &&
    (clinicLogo.startsWith("http") ||
      clinicLogo.startsWith("blob:") ||
      clinicLogo.startsWith("data:") ||
      clinicLogo.includes("/uploads/"));

  const page = {
    ...template.page,
    showSafeArea: false,
    logoSrc: usableLogo ? clinicLogo : template.page.logoSrc || DEFAULT_CERTIFICATE_LOGO,
  };

  const previewData = useMemo(
    () =>
      buildCertificatePrintData({
        patient,
        clinic,
        treatments,
        overrides: {
          leaveStartDate: formatCertificateDate(leaveStart),
          leaveEndDate: formatCertificateDate(leaveEnd),
          leaveDays: inclusiveLeaveDays(leaveStart, leaveEnd),
          reasonForLeave: reason,
          recommendationText: recommendation,
        },
      }),
    [patient, clinic, treatments, leaveStart, leaveEnd, reason, recommendation]
  );

  const handlePrint = async () => {
    setPrinting(true);
    await waitForPaint();
    await printCertificateSheet(paperRef.current);
    setPrinting(false);
  };

  if (!open) return null;

  return (
    <ModalOverlay
      className="patient-certificate-print-modal fixed inset-0 z-[70] flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="patient-certificate-print-title"
    >
      <style>
        {`@media print { @page { size: A4 ${page.orientation}; margin: 0; } }`}
      </style>
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close certificate print"
        data-no-print="true"
      />
      <div
        className={`relative z-10 flex max-h-[94dvh] w-full max-w-6xl flex-col overflow-hidden rounded-t-2xl shadow-xl sm:rounded-2xl ${
          isDark ? "bg-slate-900" : "bg-white"
        }`}
      >
        <div
          className={`flex items-center justify-between gap-3 border-b px-4 py-3 sm:px-5 ${
            isDark ? "border-slate-700" : "border-slate-200"
          }`}
          data-no-print="true"
        >
          <div>
            <h2
              id="patient-certificate-print-title"
              className={`text-base font-semibold ${isDark ? "text-slate-100" : "text-slate-900"}`}
            >
              Certificate Print
            </h2>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {previewData.patientName} · uses the saved Manage Print template
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrint}
              disabled={printing}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight px-3 py-2 text-xs font-semibold text-white hover:opacity-95 disabled:opacity-50"
            >
              <PrintIcon sx={{ fontSize: 16 }} />
              Print
            </button>
            <button
              type="button"
              onClick={onClose}
              className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                isDark
                  ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              aria-label="Close"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside
            className={`overflow-auto border-b p-4 lg:border-b-0 lg:border-r ${
              isDark ? "border-slate-700" : "border-slate-200"
            }`}
            data-no-print="true"
          >
            <label className={`mb-1 block text-xs font-semibold ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              Template
            </label>
            <select
              value={templateId}
              onChange={(event) => setTemplateId(event.target.value)}
              className={`mb-3 h-9 w-full rounded-lg border px-2 text-sm ${
                isDark
                  ? "border-slate-600 bg-slate-800 text-slate-100"
                  : "border-slate-200 bg-white text-slate-800"
              }`}
            >
              {store.templates.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                  {item.id === store.defaultId ? " (Default)" : ""}
                </option>
              ))}
            </select>
            <div className="mb-3 grid grid-cols-2 gap-2">
              <div>
                <label className={`mb-1 block text-xs font-semibold ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  Leave from
                </label>
                <input
                  type="date"
                  value={leaveStart}
                  onChange={(event) => setLeaveStart(event.target.value)}
                  className={`h-9 w-full rounded-lg border px-2 text-sm ${
                    isDark
                      ? "border-slate-600 bg-slate-800 text-slate-100"
                      : "border-slate-200 bg-white text-slate-800"
                  }`}
                />
              </div>
              <div>
                <label className={`mb-1 block text-xs font-semibold ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  Leave to
                </label>
                <input
                  type="date"
                  value={leaveEnd}
                  onChange={(event) => setLeaveEnd(event.target.value)}
                  className={`h-9 w-full rounded-lg border px-2 text-sm ${
                    isDark
                      ? "border-slate-600 bg-slate-800 text-slate-100"
                      : "border-slate-200 bg-white text-slate-800"
                  }`}
                />
              </div>
            </div>
            <p className={`mb-3 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {inclusiveLeaveDays(leaveStart, leaveEnd)} day(s)
            </p>
            <label className={`mb-1 block text-xs font-semibold ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              Reason
            </label>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className={`mb-3 h-9 w-full rounded-lg border px-2 text-sm ${
                isDark
                  ? "border-slate-600 bg-slate-800 text-slate-100"
                  : "border-slate-200 bg-white text-slate-800"
              }`}
            />
            <label className={`mb-1 block text-xs font-semibold ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              Recommendation
            </label>
            <textarea
              value={recommendation}
              onChange={(event) => setRecommendation(event.target.value)}
              className={`min-h-[72px] w-full rounded-lg border px-2 py-2 text-sm ${
                isDark
                  ? "border-slate-600 bg-slate-800 text-slate-100"
                  : "border-slate-200 bg-white text-slate-800"
              }`}
            />
          </aside>
          <div className={`overflow-auto p-4 ${isDark ? "bg-slate-950" : "bg-slate-100"}`}>
            <div className="flex justify-center">
              <CertificateCanvas
                page={page}
                elements={template.elements}
                selectedId={null}
                previewData={previewData}
                interactive={false}
                zoom={0.62}
                paperRef={paperRef}
              />
            </div>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}

export default PatientCertificatePrintModal;
